import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ChainSourceBadge, ChainSourceBanner } from "@/components/ChainSource";
import { RegimeSojournBar } from "@/components/RegimeSojournBar";
import { useIsPro } from "@/hooks/useIsPro";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, Search, ArrowUpDown, Plus, X, Banknote, TrendingUp, Target, Award } from "lucide-react";

const ROTATION_DOT = {
  STRENGTHENING: { color: "#10B981", label: "Strengthening — relative strength vs SPY accelerating (4-wk RS-Momentum)" },
  WEAKENING: { color: "#EF4444", label: "Weakening — relative strength vs SPY decelerating (4-wk RS-Momentum)" },
};

const FIELD_STATUS_LABEL = {
  ready: "Ready",
  preparing: "Preparing",
  unavailable: "Unavailable",
  insufficient_history: "Insufficient history",
  estimated: "Estimated",
  confirmed: "Confirmed",
};

function statusLabel(status) {
  if (!status) return "Preparing";
  return FIELD_STATUS_LABEL[status] || String(status).replace(/_/g, " ");
}

function FieldCell({ status, ready, children, testId }) {
  if (status === "ready" && (ready || (children != null && children !== ""))) {
    return <span data-testid={testId}>{children}</span>;
  }
  const label = statusLabel(status);
  return (
    <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}
      title={label} data-testid={testId}>{label}</span>
  );
}

const STRATEGY_OPTIONS = [
  { value: "all", label: "All Strategies" },
  { value: "bull_put_spread", label: "Bull Put Spread" },
  { value: "bull_call_spread", label: "Bull Call Spread" },
  { value: "cash_secured_put", label: "Cash-Secured Put" },
  { value: "covered_call", label: "Covered Call" },
  { value: "iron_condor", label: "Iron Condor" },
  { value: "collar", label: "Collar" },
  { value: "protective_put", label: "Protective Put" },
  { value: "bear_put_spread", label: "Bear Put Spread" },
  { value: "long_stock", label: "Long Stock" },
  { value: "flag_only", label: "Earnings Flag" },
  { value: "no_trade", label: "No Trade" },
];

export default function ScannerPage() {
  const navigate = useNavigate();
  const { requirePro } = useIsPro();
  const [results, setResults] = useState([]);
  const [chainSource, setChainSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [prepareBusy, setPrepareBusy] = useState(false);
  const [scannerPrep, setScannerPrep] = useState(null);
  const [coverageLine, setCoverageLine] = useState("");
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [strategyFilter, setStrategyFilter] = useState("all");
  const [sortField, setSortField] = useState("ticker");
  const [sortDir, setSortDir] = useState("asc");
  const [newTicker, setNewTicker] = useState("");
  const [addingTicker, setAddingTicker] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [agoTick, setAgoTick] = useState(0); // forces "Xs ago" re-render
  const [flashTickers, setFlashTickers] = useState({}); // ticker -> true (regime changed)
  const [disagreeThreshold, setDisagreeThreshold] = useState(5); // Iter201 — settings-driven
  const prevRegimes = useRef({});

  // Dividend scanner state
  const [divResults, setDivResults] = useState([]);
  const [divLoading, setDivLoading] = useState(false);
  const [divSearch, setDivSearch] = useState("");
  const [divFilter, setDivFilter] = useState("all"); // all, besti, aristocrat, wheel

  // Wheel candidates state
  const [wheelCandidates, setWheelCandidates] = useState([]);
  const [wheelLoading, setWheelLoading] = useState(false);

  // Iter180 — Moats tab state. Filters + paginated results.
  const [moatsResults, setMoatsResults] = useState([]);
  const [moatsTotal, setMoatsTotal] = useState(0);
  const [moatsLoading, setMoatsLoading] = useState(false);
  const [moatsOffset, setMoatsOffset] = useState(0);
  const [moatsFilters, setMoatsFilters] = useState({
    trailing_pct_min: -10,
    trailing_pct_max: 10,
    roe_min: 15,
    rev_growth_min: 0,
    rev_growth_max: null,   // null = no upper bound
    de_max: 1.0,
    mktcap_min_bn: 5,
  });
  const [moatsActivePreset, setMoatsActivePreset] = useState(null);
  const [moatsCacheProgress, setMoatsCacheProgress] = useState(null);

  // Iter181 — Filter presets for the Moats tab. Each preset sets ALL six
  // filters in one click; the `legend` string is shown ON the chip so the
  // user sees the thesis without hovering.
  const MOATS_PRESETS = [
    {
      key: "warren",
      label: "Warren classic",
      legend: "ROE≥20 · D/E≤0.5 · Mkt≥$25B",
      tooltip: "High-quality large caps: ROE ≥ 20 %, D/E ≤ 0.5 (conservative balance sheet), market cap ≥ $25 B. Trailing 6m band left wide (±20 %).",
      filters: { trailing_pct_min: -20, trailing_pct_max: 20, roe_min: 20, rev_growth_min: 0,
                 rev_growth_max: null, de_max: 0.5, mktcap_min_bn: 25 },
    },
    {
      key: "ai_pressured",
      label: "AI-pressured",
      legend: "ROE≥15 · Rev g ≤5% · 6m ±5%",
      tooltip: "Legacy SaaS incumbents under AI-agent disruption: still-profitable (ROE ≥ 15 %) but slowing (revenue growth ≤ 5 %) and trading flat (6m within ±5 %). D/E ≤ 1.5 permissive.",
      filters: { trailing_pct_min: -5, trailing_pct_max: 5, roe_min: 15, rev_growth_min: -20,
                 rev_growth_max: 5, de_max: 1.5, mktcap_min_bn: 5 },
    },
    {
      key: "deep_value",
      label: "Deep value",
      legend: "6m ≤ -10% · D/E≤1 · ROE≥10",
      tooltip: "Beaten-down but not broken: 6-month trailing return ≤ -10 %, ROE ≥ 10 %, D/E ≤ 1. Market cap ≥ $2 B keeps micro-caps out.",
      filters: { trailing_pct_min: -100, trailing_pct_max: -10, roe_min: 10, rev_growth_min: -20,
                 rev_growth_max: null, de_max: 1, mktcap_min_bn: 2 },
    },
  ];

  const applyMoatsPreset = (presetKey) => {
    const preset = MOATS_PRESETS.find(p => p.key === presetKey);
    if (!preset) return;
    setMoatsFilters(prev => ({ ...prev, ...preset.filters }));
    setMoatsActivePreset(presetKey);
    // Defer the load until after the state has flushed so we don't send
    // the stale filter payload.
    setTimeout(() => loadMoats(0), 50);
  };

  // Iter181 — wrapper that clears the active preset badge whenever the
  // user manually tweaks a filter, so the chip visual never lies about
  // what's currently in effect.
  const updateMoatsFilter = (field, value) => {
    setMoatsFilters(f => ({ ...f, [field]: value }));
    if (moatsActivePreset) setMoatsActivePreset(null);
  };

  // Watchlist group state — "My Watchlist" (editable) + read-only sector presets.
  const [activeGroup, setActiveGroup] = useState("My Watchlist");
  const [groups, setGroups] = useState([]);
  const activeGroupRef = useRef("My Watchlist");
  const loadSeq = useRef(0);
  const [rotationMap, setRotationMap] = useState({}); // sector -> {signal, momentum, excess_1m}

  // Iter201 — never show a stale regime value: stale rows get a skeleton on
  // the regime cell while a background per-ticker refresh resolves the real
  // snapshot (batches of 3, capped at 12 per load).
  const groupCache = useRef({});
  const scannerAbort = useRef(null);

  const placeholdersFor = (g) => {
    const found = groups.find((x) => x.name === g);
    const tickers = found?.tickers || [];
    return tickers.map((t) => ({
      ticker: t,
      cache_status: "missing",
      field_status: { quote: "preparing", technical: "preparing", fundamental: "preparing", hmm: "preparing", options: "preparing" },
    }));
  };

  const applyResults = (rows) => {
    // Detect regime changes vs the previous snapshot and flash those rows.
    const changed = {};
    const nextRegimes = {};
    rows.forEach(r => {
      if (!r.ticker) return;
      const shown = r.state_5d || r.regime_label;
      nextRegimes[r.ticker] = shown;
      const prev = prevRegimes.current[r.ticker];
      if (prev && shown && prev !== shown) changed[r.ticker] = true;
    });
    prevRegimes.current = nextRegimes;
    setResults(rows);
    // newest refresh timestamp across rows
    const ts = rows.map(r => r.last_refreshed).filter(Boolean).sort().pop();
    if (ts) setLastRefreshedAt(new Date(ts).getTime());
    if (Object.keys(changed).length) {
      setFlashTickers(changed);
      setTimeout(() => setFlashTickers({}), 1200);
    }
  };

  const loadScanner = async (grp, opts) => {
    const silent = Boolean(opts && opts.silent);
    const g = grp ?? activeGroupRef.current;
    const seq = ++loadSeq.current;
    const cachedRows = groupCache.current[g];
    setLoadError(null);
    if (cachedRows) {
      applyResults(cachedRows);
      setLoading(false);
    } else {
      const ph = placeholdersFor(g);
      if (ph.length) {
        applyResults(ph);
        setLoading(false);
      } else if (!silent) {
        setLoading(true);
      }
    }
    if (scannerAbort.current) scannerAbort.current.abort();
    const ac = new AbortController();
    scannerAbort.current = ac;
    try {
      const params = (g && g !== "My Watchlist") ? { group: g } : {};
      const { data } = await api.get("/scanner", { params, timeout: 8000, signal: ac.signal });
      if (seq !== loadSeq.current) return;
      setChainSource(data.chain_source || null);
      const rows = data.results || [];
      groupCache.current[g] = rows;
      applyResults(rows);
      setScannerPrep(data.preparation || null);
      setCoverageLine(data.preparation?.coverage_line || "");
      setLoadError(null);
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
      if (seq !== loadSeq.current) return;
      console.error("Scanner error:", err);
      const timedOut = err?.code === "ECONNABORTED" || /timeout/i.test(String(err?.message || ""));
      setLoadError(timedOut
        ? "Scanner timed out. Cached rows below may be incomplete — retry."
        : "Scanner failed to load. Retry to fetch cached rows.");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  };

  const selectGroup = (name) => {
    if (name === activeGroup) return;
    setActiveGroup(name);
    activeGroupRef.current = name;
    setSearch("");
    loadScanner(name);
  };

  const loadDividendScanner = async () => {
    setDivLoading(true);
    try {
      const { data } = await api.get("/scanner/dividend");
      setDivResults(data.results || []);
    } catch (err) { console.error("Dividend scanner error:", err); }
    finally { setDivLoading(false); }
  };

  const loadWheelCandidates = async () => {
    setWheelLoading(true);
    try {
      const { data } = await api.get("/scanner/wheel-candidates");
      setWheelCandidates(data.candidates || []);
    } catch (err) { console.error("Wheel candidates error:", err); }
    finally { setWheelLoading(false); }
  };

  // Iter180 — Load a page of Moats results. `offset` supports 20/40/60/80
  // paging up to the 100-row hard cap. Filters are read from state so
  // re-clicking "Refresh" applies edits without a separate submit button.
  const loadMoats = async (offset = 0) => {
    if (!requirePro("/scanner/moats")) return;
    setMoatsLoading(true);
    try {
      const params = new URLSearchParams({
        trailing_pct_min: moatsFilters.trailing_pct_min,
        trailing_pct_max: moatsFilters.trailing_pct_max,
        roe_min: moatsFilters.roe_min,
        rev_growth_min: moatsFilters.rev_growth_min,
        de_max: moatsFilters.de_max,
        mktcap_min_bn: moatsFilters.mktcap_min_bn,
        offset,
        limit: 20,
      });
      // Only append the optional upper bound when set (null = no cap).
      if (moatsFilters.rev_growth_max != null) {
        params.set("rev_growth_max", moatsFilters.rev_growth_max);
      }
      const { data } = await api.get(`/scanner/moats?${params.toString()}`);
      if (offset === 0) setMoatsResults(data.results || []);
      else setMoatsResults(prev => [...prev, ...(data.results || [])]);
      setMoatsTotal(data.total || 0);
      setMoatsOffset(offset);
      setMoatsCacheProgress(data.cache_progress || null);
    } catch (err) { console.error("Moats scanner error:", err); }
    finally { setMoatsLoading(false); }
  };

  useEffect(() => {
    loadScanner();
    api.get("/scanner/groups").then(({ data }) => setGroups(data.groups || [])).catch(() => {});
    api.get("/settings").then(({ data }) => setDisagreeThreshold(data.regime_disagree_threshold_pct ?? 5)).catch(() => {});
  }, []);

  // Tick the "updated Xs ago" label every 5s while we have a timestamp.
  useEffect(() => {
    if (!lastRefreshedAt) return;
    const id = setInterval(() => setAgoTick(t => t + 1), 5000);
    return () => clearInterval(id);
  }, [lastRefreshedAt]);

  const agoLabel = useMemo(() => {
    if (!lastRefreshedAt) return null;
    const s = Math.max(0, Math.round((Date.now() - lastRefreshedAt) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    return m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`;
  }, [lastRefreshedAt, agoTick]);

  const loadScannerRef = useRef(loadScanner);
  loadScannerRef.current = loadScanner;
  useEffect(() => {
    if (!(scannerPrep?.queued > 0)) return undefined;
    const id = setInterval(() => {
      delete groupCache.current[activeGroupRef.current];
      loadScannerRef.current(undefined, { silent: true });
    }, 8000);
    return () => clearInterval(id);
  }, [scannerPrep?.queued, scannerPrep?.completed, activeGroup]);

  const handleReload = async () => {
    setRefreshing(true);
    try {
      delete groupCache.current[activeGroupRef.current];
      await loadScanner();
    } catch (err) { console.error("Reload error:", err); }
    finally { setRefreshing(false); }
  };

  const handlePrepareWatchlist = async () => {
    setPrepareBusy(true);
    try {
      await api.post("/scanner/prepare-watchlist", { group: activeGroupRef.current }, { timeout: 5000 });
      delete groupCache.current[activeGroupRef.current];
      await loadScanner();
    } catch (err) { console.error("Prepare watchlist error:", err); }
    finally { setPrepareBusy(false); }
  };

  const handleAddTicker = async () => {
    if (!newTicker.trim()) return;
    setAddingTicker(true);
    try {
      await api.post("/watchlist/add", { ticker: newTicker.toUpperCase() });
      setNewTicker("");
      await loadScanner();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not add ticker";
      window.alert(detail);
    }
    finally { setAddingTicker(false); }
  };

  const handleRemoveTicker = async (ticker) => {
    try {
      await api.delete(`/watchlist/${ticker}`);
      setResults(prev => prev.filter(r => r.ticker !== ticker));
    } catch (err) { console.error("Remove ticker error:", err); }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    let data = results.filter(r => !r.error);
    if (search) data = data.filter(r => r.ticker?.toLowerCase().includes(search.toLowerCase()));
    if (strategyFilter !== "all") data = data.filter(r => r.recommended_strategy === strategyFilter);
    data.sort((a, b) => {
      let va = a[sortField] ?? "", vb = b[sortField] ?? "";
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      return sortDir === "asc" ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
    });
    return data;
  }, [results, search, strategyFilter, sortField, sortDir]);



  const filteredDiv = useMemo(() => {
    let data = divResults;
    if (divSearch) data = data.filter(r => r.ticker?.toLowerCase().includes(divSearch.toLowerCase()));
    if (divFilter === "besti") data = data.filter(r => r.is_besti);
    if (divFilter === "aristocrat") data = data.filter(r => r.is_aristocrat);
    if (divFilter === "wheel") data = data.filter(r => r.wheel_friendly);
    return data;
  }, [divResults, divSearch, divFilter]);

  const SortHeader = ({ field, label, align }) => (
    <th className={`text-xs font-medium cursor-pointer select-none hover:text-[var(--hoip-text-primary)] transition-colors ${align === "right" ? "text-right" : "text-left"}`}
      style={{ color: sortField === field ? "var(--hoip-text-primary)" : "var(--hoip-text-tertiary)" }}
      onClick={() => toggleSort(field)}>
      <span className="inline-flex items-center gap-1">{label}{sortField === field && <ArrowUpDown className="w-3 h-3" />}</span>
    </th>
  );

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4" data-testid="scanner-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Scanner</h1>
        <ChainSourceBadge source={chainSource} />
      </div>
      <ChainSourceBanner source={chainSource} />

      <Tabs defaultValue="momentum" onValueChange={(v) => {
        if (v === "dividend" && divResults.length === 0) loadDividendScanner();
        if (v === "wheel" && wheelCandidates.length === 0) loadWheelCandidates();
        if (v === "moats" && moatsResults.length === 0) loadMoats(0);
      }}>
        <TabsList className="bg-[var(--hoip-card)] border border-[var(--hoip-border)] rounded-sm h-9">
          <TabsTrigger value="momentum" className="text-xs rounded-sm data-[state=active]:bg-[var(--hoip-hover)]" data-testid="tab-momentum">
            <TrendingUp className="w-3 h-3 mr-1" /> Momentum
          </TabsTrigger>
          <TabsTrigger value="dividend" className="text-xs rounded-sm data-[state=active]:bg-[var(--hoip-hover)]" data-testid="tab-dividend">
            <Banknote className="w-3 h-3 mr-1" /> BESTI / Dividends
          </TabsTrigger>
          <TabsTrigger value="wheel" className="text-xs rounded-sm data-[state=active]:bg-[var(--hoip-hover)]" data-testid="tab-wheel">
            <Target className="w-3 h-3 mr-1" /> Wheel Candidates
          </TabsTrigger>
          <TabsTrigger value="moats" className="text-xs rounded-sm data-[state=active]:bg-[var(--hoip-hover)]" data-testid="tab-moats">
            <Award className="w-3 h-3 mr-1" /> Moats
          </TabsTrigger>
        </TabsList>

        {/* ═══ MOMENTUM SCANNER ═══ */}
        <TabsContent value="momentum" className="mt-3 space-y-3">
          {/* Watchlist group selector — editable "My Watchlist" + read-only sector presets */}
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="scanner-group-selector">
            <span className="text-[10px] uppercase tracking-wide mr-1" style={{ color: "var(--hoip-text-tertiary)" }}>Watchlists</span>
            {[{ name: "My Watchlist", count: null }, { name: "Portfolio", count: null }, ...groups].map(g => {
              const active = activeGroup === g.name;
              const rot = ROTATION_DOT[rotationMap[g.name]?.signal];
              return (
                <button key={g.name} type="button" onClick={() => selectGroup(g.name)}
                  data-testid={`group-chip-${g.name.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
                  title={g.name === "My Watchlist" ? "Your personal, editable watchlist"
                    : `${g.name} — top ${g.count} names by market cap (read-only preset)${rot ? ` · ${rot.label}` : ""}`}
                  className="text-[11px] px-2 py-0.5 rounded-sm border transition-colors whitespace-nowrap inline-flex items-center gap-1.5"
                  style={{
                    color: active ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
                    background: active ? "var(--hoip-text-primary)" : "var(--hoip-card)",
                    borderColor: active ? "var(--hoip-text-primary)" : "var(--hoip-border)",
                  }}>
                  {rot && <span className="w-1.5 h-1.5 rounded-full inline-block shrink-0" style={{ background: rot.color }}
                    data-testid={`rotation-dot-${g.name.replace(/[^a-zA-Z]/g, "").toLowerCase()}`} />}
                  {g.name}{g.count != null && <span className="ml-1 opacity-60 font-mono-data">{g.count}</span>}
                </button>
              );
            })}
            {Object.keys(rotationMap).length > 0 && (
              <span className="inline-flex items-center gap-2 ml-1 text-[9px]" data-testid="rotation-legend" style={{ color: "var(--hoip-text-tertiary)" }}>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: "#10B981" }} /> rotating in</span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: "#EF4444" }} /> rotating out</span>
              </span>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="relative flex-shrink-0">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--hoip-text-tertiary)" }} />
              <Input data-testid="scanner-search" placeholder="Search ticker..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 w-36 bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm text-xs" style={{ color: "var(--hoip-text-primary)" }} />
            </div>
            <select data-testid="scanner-strategy-filter" value={strategyFilter} onChange={(e) => setStrategyFilter(e.target.value)}
              className="h-8 px-2 text-xs rounded-sm border outline-none w-44"
              style={{ background: "var(--hoip-bg)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}>
              {STRATEGY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <Button data-testid="scanner-refresh-all" variant="outline" size="sm"
              onClick={handleReload} disabled={refreshing}
              title="Re-read the latest Scanner rows from Mongo. Zero provider calls."
              className="h-8 border-[var(--hoip-border)] text-xs rounded-sm hover:bg-[var(--hoip-hover)]">
              <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Reload cached results
            </Button>
            <Button data-testid="scanner-prepare-watchlist" variant="outline" size="sm"
              onClick={handlePrepareWatchlist} disabled={prepareBusy}
              title="Join or create one shared preparation batch (up to 8 tickers) for this watchlist. Repeated clicks join the same work."
              className="h-8 border-[var(--hoip-border)] text-xs rounded-sm hover:bg-[var(--hoip-hover)]">
              <RefreshCw className={`w-3 h-3 mr-1 ${prepareBusy ? "animate-spin" : ""}`} /> Prepare this watchlist
            </Button>
            <span className="text-[11px] flex items-center gap-1.5" data-testid="scanner-refresh-status" style={{ color: "var(--hoip-text-tertiary)" }}>
              {refreshing ? (
                <><RefreshCw className="w-3 h-3 animate-spin" /> Reloading cache…</>
              ) : agoLabel ? (
                <><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" /> Cache as-of {agoLabel} · delayed/cache-backed, not live</>
              ) : (
                <>Delayed/cache-backed — not live pricing</>
              )}
            </span>
          </div>

          {activeGroup === "My Watchlist" ? (
            <div className="flex items-center gap-2">
              <Input data-testid="scanner-add-ticker-input" placeholder="Add ticker (max 20)..." value={newTicker}
                onChange={(e) => setNewTicker(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && handleAddTicker()}
                className="h-8 w-40 bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm text-xs font-mono-data" style={{ color: "var(--hoip-text-primary)" }} />
              <Button data-testid="scanner-add-ticker-btn" size="sm" onClick={handleAddTicker} disabled={addingTicker}
                className="h-8 bg-white text-black hover:bg-gray-200 rounded-sm text-xs"><Plus className="w-3 h-3 mr-1" /> Add</Button>
            </div>
          ) : activeGroup === "Portfolio" ? (
            <div className="text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="scanner-preset-note">
              Holdings from your portfolio — joined to the shared market cache by ticker.
            </div>
          ) : (
            <div className="text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="scanner-preset-note">
              Read-only preset — top {groups.find(g => g.name === activeGroup)?.count || 20} {activeGroup} names by market cap. Switch to <span className="font-medium" style={{ color: "var(--hoip-text-secondary)" }}>My Watchlist</span> to add or remove tickers.
            </div>
          )}

          {loadError ? (
            <div className="text-[11px] flex items-center gap-2" style={{ color: "#F87171" }}
              data-testid="scanner-load-error">
              <span>{loadError}</span>
              <Button data-testid="scanner-retry" variant="outline" size="sm"
                onClick={() => loadScanner()}
                className="h-7 border-[var(--hoip-border)] text-xs rounded-sm">Retry</Button>
            </div>
          ) : null}

          {coverageLine ? (
            <div className="text-[10px] whitespace-pre-line" style={{ color: "var(--hoip-text-secondary)" }}
              data-testid="scanner-coverage-line">
              {coverageLine}
              {scannerPrep?.disclaimer ? (
                <div className="mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>{scannerPrep.disclaimer}</div>
              ) : null}
            </div>
          ) : null}

          <div className="border overflow-x-auto" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
            {loading && results.length === 0 ? (
              <div className="flex items-center justify-center py-8" data-testid="scanner-skeleton">
                <div className="h-4 w-48 rounded-sm animate-pulse" style={{ background: "var(--hoip-hover)" }} />
              </div>
            ) : (
              <table className="w-full dense-table" data-testid="scanner-table">
                <thead className="sticky top-0 z-10" style={{ background: "var(--hoip-card)" }}>
                  <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                    <SortHeader field="ticker" label="Ticker" />
                    <SortHeader field="sector" label="Sector" />
                    <SortHeader field="current_price" label="Price" align="right" />
                    <SortHeader field="daily_change_pct" label="Chg%" align="right" />
                    <SortHeader field="technical_score" label="Tech" align="right" />
                    <SortHeader field="fundamental_score" label="Fund" align="right" />
                    <SortHeader field="days_to_earnings" label="Earn" align="right" />
                    <SortHeader field="state_5d" label="Regime" />
                    <SortHeader field="recommended_strategy" label="Strategy" />
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    const st = r.field_status || {};
                    return (
                    <tr key={r.ticker || i} className="border-b hover:bg-[var(--hoip-hover)] cursor-pointer"
                      style={{
                        borderColor: "var(--hoip-divider)",
                        transition: "box-shadow 0.2s ease, background-color 0.2s ease, opacity 0.2s",
                        opacity: refreshing ? 0.55 : 1,
                        boxShadow: flashTickers[r.ticker] ? "inset 0 0 0 2px #F59E0B" : "none",
                      }}
                      onClick={() => navigate(`/ticker/${r.ticker}`)} data-testid={`scanner-row-${r.ticker}`}>
                      <td className="font-mono-data text-xs font-medium">
                        <span>{r.ticker}</span>
                        {flashTickers[r.ticker] && <span className="ml-1 text-[9px] text-amber-500" data-testid={`regime-changed-${r.ticker}`}>↻ regime</span>}
                      </td>
                      <td className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>{r.sector}</td>
                      <td className="text-right font-mono-data text-xs">
                        <FieldCell status={st.quote} ready={r.current_price != null} testId={`quote-status-${r.ticker}`}>
                          {r.current_price != null ? `$${Number(r.current_price).toFixed(2)}` : null}
                        </FieldCell>
                      </td>
                      <td className={`text-right font-mono-data text-xs ${r.daily_change_pct == null ? "" : (r.daily_change_pct >= 0 ? "text-emerald-500" : "text-red-500")}`}>
                        <FieldCell status={st.quote} ready={r.daily_change_pct != null} testId={`chg-status-${r.ticker}`}>
                          {r.daily_change_pct == null ? null : `${r.daily_change_pct >= 0 ? "+" : ""}${Number(r.daily_change_pct).toFixed(2)}%`}
                        </FieldCell>
                      </td>
                      <td className="text-right font-mono-data text-xs">
                        <FieldCell status={st.technical} ready={r.technical_score != null} testId={`tech-status-${r.ticker}`}>
                          {r.technical_score}
                        </FieldCell>
                      </td>
                      <td className="text-right font-mono-data text-xs">
                        <FieldCell status={st.fundamental} ready={r.fundamental_score != null} testId={`fund-status-${r.ticker}`}>
                          {r.fundamental_score}
                        </FieldCell>
                      </td>
                      <td className="text-right font-mono-data text-xs">
                        {st.earnings === "confirmed" || st.earnings === "estimated" ? (
                          <span className={r.days_to_earnings != null && r.days_to_earnings <= 14 ? "text-yellow-500" : ""}
                            data-testid={`earn-status-${r.ticker}`}
                            title={st.earnings === "confirmed" ? "Confirmed" : "Estimated"}>
                            {r.days_to_earnings != null ? `${r.days_to_earnings}d` : statusLabel(st.earnings)}
                            <span className="ml-0.5 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                              {st.earnings === "confirmed" ? "conf" : "est"}
                            </span>
                          </span>
                        ) : (
                          <FieldCell status={st.earnings || "preparing"} testId={`earn-status-${r.ticker}`} />
                        )}
                      </td>
                      <td>
                        {st.hmm === "ready" ? (() => {
                          const shown = r.state_5d || r.regime_label;
                          if (!shown) return <FieldCell status="preparing" testId={`hmm-status-${r.ticker}`} />;
                          const rc = { "Quiet Range": "#3B82F6", "Bullish Trend": "#10B981", "Bearish Trend": "#EF4444", "Volatile": "#F59E0B" };
                          const disagreeNow = r.state_5d && r.regime_label && r.state_5d !== r.regime_label;
                          const flagged = disagreeNow && (r.disagree_pct_60d ?? 0) >= disagreeThreshold;
                          return (
                            <>
                              <span className="text-[10px] font-mono-data px-1 py-0.5 rounded-sm" data-testid={`regime-badge-${r.ticker}`}
                                style={{ color: rc[shown] || "#71717A", background: `${rc[shown] || "#71717A"}15` }}>
                                {shown}{!r.state_5d && r.regime_prob ? ` ${Math.round(r.regime_prob * 100)}%` : ""}
                              </span>
                              {flagged && (
                                <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full align-middle cursor-help"
                                  style={{ background: "#F59E0B" }}
                                  title={`5d: ${r.state_5d} · Instant: ${r.regime_label}${r.regime_prob ? ` (${Math.round(r.regime_prob * 100)}%)` : ""} — ${r.disagree_pct_60d}% disagreement over last 60 bars (threshold ${disagreeThreshold}%, set in Settings → Risk Limits)`}
                                  data-testid={`regime-disagree-flag-${r.ticker}`} />
                              )}
                            </>
                          );
                        })() : (
                          <FieldCell status={st.hmm || "preparing"} testId={`hmm-status-${r.ticker}`} />
                        )}
                        {r.is_stale && st.quote === "ready" && (
                          <span className="ml-1 text-[9px] font-mono-data px-1 py-0.5 rounded-sm"
                            style={{ color: "var(--hoip-text-tertiary)", background: "var(--hoip-hover)" }}
                            title="Stored provider-as-of is older than 45 minutes — list still served from Mongo"
                            data-testid={`stale-${r.ticker}`}>
                            Delayed
                          </span>
                        )}
                        {st.hmm === "ready" && (
                          <RegimeSojournBar regime={r.state_5d || r.regime_label} elapsedDays={r.regime_age_bars}
                            medianSojournDays={r.median_sojourn_days} dte={35} transitionProbs={r.transition_probs} />
                        )}
                      </td>
                      <td>
                        {st.options === "ready" ? (
                          <>
                            <span className={`text-xs capitalize px-1.5 py-0.5 border font-mono-data ${
                              r.recommended_strategy === "no_trade" ? "text-[var(--hoip-text-tertiary)] border-[var(--hoip-border)]" :
                              r.recommended_strategy === "flag_only" ? "text-yellow-500 border-yellow-800" :
                              "text-emerald-500 border-emerald-800"
                            }`}>{(r.strategy_display || r.recommended_strategy || "—").replace(/_/g, " ")}</span>
                            {r.strategy_score >= 70 && <span className="ml-1 text-[9px] font-mono-data text-emerald-500">{r.strategy_score}</span>}
                          </>
                        ) : (
                          <FieldCell status={st.options || "preparing"} testId={`options-status-${r.ticker}`} />
                        )}
                      </td>
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {activeGroup === "My Watchlist" && (
                            <button onClick={() => handleRemoveTicker(r.ticker)} className="p-1 hover:bg-[var(--hoip-hover)] rounded-sm" data-testid={`remove-${r.ticker}`}>
                              <X className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-8 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>No results. Try adjusting filters or refreshing.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* ═══ DIVIDEND / BESTI SCANNER ═══ */}
        <TabsContent value="dividend" className="mt-3 space-y-3">
          <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
            <h2 className="text-xs font-bold mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>BESTI + Dividend Aristocrats Scanner</h2>
            <p className="text-[10px] mb-3" style={{ color: "var(--hoip-text-tertiary)" }}>
              Banks, Energy, Sewerage/Utilities, Telecom, Insurance — stocks with consistent dividends + good option liquidity for wheel strategy income.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-shrink-0">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--hoip-text-tertiary)" }} />
                <Input data-testid="div-search" placeholder="Search..." value={divSearch} onChange={(e) => setDivSearch(e.target.value)}
                  className="pl-8 h-8 w-36 bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm text-xs" style={{ color: "var(--hoip-text-primary)" }} />
              </div>
              <select data-testid="div-filter" value={divFilter} onChange={(e) => setDivFilter(e.target.value)}
                className="h-8 px-2 text-xs rounded-sm border outline-none w-48"
                style={{ background: "var(--hoip-bg)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}>
                <option value="all">All ({divResults.length})</option>
                <option value="besti">BESTI Sectors ({divResults.filter(r => r.is_besti).length})</option>
                <option value="aristocrat">Dividend Aristocrats ({divResults.filter(r => r.is_aristocrat).length})</option>
                <option value="wheel">Wheel-Friendly ({divResults.filter(r => r.wheel_friendly).length})</option>
              </select>
              <Button variant="outline" size="sm" onClick={loadDividendScanner} disabled={divLoading}
                className="h-8 border-[var(--hoip-border)] text-xs rounded-sm hover:bg-[var(--hoip-hover)]" data-testid="div-refresh-btn">
                <RefreshCw className={`w-3 h-3 mr-1 ${divLoading ? "animate-spin" : ""}`} />
                {divLoading ? "Scanning..." : "Refresh"}
              </Button>
            </div>
          </div>

          <div className="border overflow-x-auto" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
            {divLoading && divResults.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-5 h-5 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />
                <span className="ml-2 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>Scanning BESTI + Aristocrat tickers...</span>
              </div>
            ) : (
              <table className="w-full dense-table" data-testid="dividend-table">
                <thead className="sticky top-0 z-10" style={{ background: "var(--hoip-card)" }}>
                  <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                    <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</th>
                    <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Sector</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Price</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Div Yield</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Payout %</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Opt Vol</th>
                    <th className="text-center text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>BESTI</th>
                    <th className="text-center text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Arist</th>
                    <th className="text-center text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Wheel</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDiv.map((r, i) => (
                    <tr key={r.ticker || i} className="border-b hover:bg-[var(--hoip-hover)] transition-colors cursor-pointer"
                      style={{ borderColor: "var(--hoip-divider)" }} onClick={() => navigate(`/ticker/${r.ticker}`)}>
                      <td className="font-mono-data text-xs font-medium">{r.ticker}</td>
                      <td className="text-xs truncate max-w-[100px]" style={{ color: "var(--hoip-text-secondary)" }}>{r.sector}</td>
                      <td className="text-right font-mono-data text-xs">${(r.price || 0).toFixed(2)}</td>
                      <td className={`text-right font-mono-data text-xs ${(r.dividend_yield || 0) >= 3 ? "text-emerald-500" : ""}`}>
                        {r.dividend_yield != null ? `${r.dividend_yield}%` : "—"}
                      </td>
                      <td className="text-right font-mono-data text-xs">{r.payout_ratio != null ? `${r.payout_ratio}%` : "—"}</td>
                      <td className={`text-right font-mono-data text-xs ${r.good_option_liquidity ? "text-emerald-500" : "text-[var(--hoip-text-tertiary)]"}`}>
                        {(r.option_volume || 0).toLocaleString()}
                      </td>
                      <td className="text-center text-xs">{r.is_besti ? <span className="text-blue-400">Y</span> : <span style={{ color: "var(--hoip-text-tertiary)" }}>—</span>}</td>
                      <td className="text-center text-xs">{r.is_aristocrat ? <span className="text-yellow-400">Y</span> : <span style={{ color: "var(--hoip-text-tertiary)" }}>—</span>}</td>
                      <td className="text-center text-xs">{r.wheel_friendly ? <span className="text-emerald-500 font-bold">Y</span> : <span style={{ color: "var(--hoip-text-tertiary)" }}>—</span>}</td>
                    </tr>
                  ))}
                  {filteredDiv.length === 0 && !divLoading && (
                    <tr><td colSpan={9} className="text-center py-8 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>
                      {divResults.length === 0 ? "Click Refresh to scan BESTI + Dividend Aristocrat tickers." : "No results match the filter."}
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* ═══ WHEEL CANDIDATES SCANNER ═══ */}
        <TabsContent value="wheel" className="mt-3 space-y-3" data-testid="wheel-candidates-tab">
          <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
            <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: "var(--hoip-border)" }}>
              <h2 className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Wheel Candidates — Matches CSP/CC Screening Criteria</h2>
              <Button variant="outline" size="sm" onClick={loadWheelCandidates} disabled={wheelLoading}
                className="h-7 text-[10px] rounded-sm" data-testid="wheel-refresh-btn">
                <RefreshCw className={`w-3 h-3 mr-1 ${wheelLoading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
            {wheelLoading && <div className="p-6 text-center text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>Scanning wheel candidates...</div>}
            {!wheelLoading && (
              <table className="w-full text-left" data-testid="wheel-candidates-table">
                <thead>
                  <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                    <th className="text-left text-xs font-medium px-3 py-2" style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</th>
                    <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Sector</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Price</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Wheel Score</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Fund</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Tech</th>
                    <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Regime</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>CSP Strike</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Yield</th>
                    <th className="text-center text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {wheelCandidates.map((c, i) => (
                    <tr key={i} className="border-b hover:bg-[var(--hoip-hover)] cursor-pointer transition-colors"
                      style={{ borderColor: "var(--hoip-border)" }}
                      onClick={() => navigate(`/ticker/${c.ticker}`)}
                      data-testid={`wheel-row-${c.ticker}`}>
                      <td className="font-mono-data text-xs font-medium px-3 py-1.5 text-blue-400">{c.ticker}</td>
                      <td className="text-xs truncate max-w-[80px]" style={{ color: "var(--hoip-text-secondary)" }}>{c.sector}</td>
                      <td className="text-right font-mono-data text-xs">${c.price.toFixed(2)}</td>
                      <td className="text-right">
                        <span className={`font-mono-data text-xs font-bold ${c.wheel_score >= 70 ? "text-emerald-500" : c.wheel_score >= 50 ? "text-yellow-500" : "text-red-400"}`}>{c.wheel_score}</span>
                      </td>
                      <td className={`text-right font-mono-data text-xs ${c.fund_score >= 7 ? "text-emerald-500" : c.fund_score >= 6 ? "text-yellow-500" : "text-red-400"}`}>{c.fund_score}</td>
                      <td className={`text-right font-mono-data text-xs ${c.tech_score >= 7 ? "text-emerald-500" : c.tech_score >= 6 ? "text-yellow-500" : "text-red-400"}`}>{c.tech_score}</td>
                      <td className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>{c.regime}</td>
                      <td className="text-right font-mono-data text-xs">${c.csp_strike}</td>
                      <td className={`text-right font-mono-data text-xs ${c.premium_yield >= 1.0 ? "text-emerald-500" : "text-yellow-500"}`}>{c.premium_yield}%</td>
                      <td className="text-center">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${
                          c.status === "OPEN" ? "bg-emerald-500/10 text-emerald-400" :
                          c.status === "ACTIVE" ? "bg-blue-500/10 text-blue-400" :
                          "bg-yellow-500/10 text-yellow-500"
                        }`} data-testid={`wheel-status-${c.ticker}`}>{c.status}</span>
                      </td>
                    </tr>
                  ))}
                  {wheelCandidates.length === 0 && !wheelLoading && (
                    <tr><td colSpan={10} className="text-center py-8 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>
                      Click Refresh to scan wheel candidates.
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* ═══ MOATS SCANNER (Iter180) ═══ */}
        <TabsContent value="moats" className="mt-3 space-y-3" data-testid="moats-tab">
          <div className="border rounded-sm p-3 space-y-2" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Moats — Quality-but-Sideways Screener</h2>
              <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                Cross-sector · sorted by market cap · 20 per page (100 max)
              </span>
            </div>

            {/* Iter181 — Filter preset chips. Each chip carries its criteria
                as an inline legend so users see the thesis at a glance;
                clicking applies all six filters and re-fetches. */}
            <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="moats-preset-chips">
              <span className="text-[9px] uppercase tracking-wide mr-1" style={{ color: "var(--hoip-text-tertiary)" }}>Presets</span>
              {MOATS_PRESETS.map((p) => {
                const active = moatsActivePreset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => applyMoatsPreset(p.key)}
                    title={p.tooltip}
                    data-testid={`moats-preset-${p.key}`}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] border transition-colors ${
                      active
                        ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300"
                        : "border-[var(--hoip-border)] hover:bg-[var(--hoip-hover)]"
                    }`}
                    style={{ color: active ? undefined : "var(--hoip-text-secondary)" }}>
                    <span className="font-medium">{p.label}</span>
                    <span className="opacity-60 font-mono-data text-[9px]">·</span>
                    <span className="opacity-70 font-mono-data text-[9px]">{p.legend}</span>
                  </button>
                );
              })}
              {moatsActivePreset && (
                <button
                  type="button"
                  onClick={() => setMoatsActivePreset(null)}
                  className="text-[9px] underline opacity-60 hover:opacity-100 ml-1"
                  style={{ color: "var(--hoip-text-tertiary)" }}
                  data-testid="moats-preset-clear">
                  clear
                </button>
              )}
            </div>
            {/* Filters row */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              <div>
                <label className="text-[9px] uppercase tracking-wide block" style={{ color: "var(--hoip-text-tertiary)" }}>Trailing 6m min %</label>
                <Input data-testid="moats-filter-trailing-min" type="number" step="1" value={moatsFilters.trailing_pct_min}
                  onChange={(e) => updateMoatsFilter("trailing_pct_min", parseFloat(e.target.value))}
                  className="h-7 text-xs font-mono-data bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm" />
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-wide block" style={{ color: "var(--hoip-text-tertiary)" }}>Trailing 6m max %</label>
                <Input data-testid="moats-filter-trailing-max" type="number" step="1" value={moatsFilters.trailing_pct_max}
                  onChange={(e) => updateMoatsFilter("trailing_pct_max", parseFloat(e.target.value))}
                  className="h-7 text-xs font-mono-data bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm" />
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-wide block" style={{ color: "var(--hoip-text-tertiary)" }}>ROE min %</label>
                <Input data-testid="moats-filter-roe-min" type="number" step="1" value={moatsFilters.roe_min}
                  onChange={(e) => updateMoatsFilter("roe_min", parseFloat(e.target.value))}
                  className="h-7 text-xs font-mono-data bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm" />
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-wide block" style={{ color: "var(--hoip-text-tertiary)" }}>Rev growth min %</label>
                <Input data-testid="moats-filter-rev-growth-min" type="number" step="1" value={moatsFilters.rev_growth_min}
                  onChange={(e) => updateMoatsFilter("rev_growth_min", parseFloat(e.target.value))}
                  className="h-7 text-xs font-mono-data bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm" />
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-wide block" style={{ color: "var(--hoip-text-tertiary)" }}>D/E max (ratio)</label>
                <Input data-testid="moats-filter-de-max" type="number" step="0.1" value={moatsFilters.de_max}
                  onChange={(e) => updateMoatsFilter("de_max", parseFloat(e.target.value))}
                  className="h-7 text-xs font-mono-data bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm" />
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-wide block" style={{ color: "var(--hoip-text-tertiary)" }}>Mkt cap min ($B)</label>
                <Input data-testid="moats-filter-mktcap-min" type="number" step="1" value={moatsFilters.mktcap_min_bn}
                  onChange={(e) => updateMoatsFilter("mktcap_min_bn", parseFloat(e.target.value))}
                  className="h-7 text-xs font-mono-data bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm" />
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-wide block" style={{ color: "var(--hoip-text-tertiary)" }}>Moat rating</label>
                <select disabled className="h-7 w-full text-xs rounded-sm border opacity-60 cursor-not-allowed"
                  style={{ background: "var(--hoip-bg)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}
                  data-testid="moats-filter-moat-rating" title="Coming soon — requires Morningstar-style moat feed">
                  <option>Coming soon</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-[10px] flex items-center gap-2" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="moats-result-count">
                {moatsTotal > 0 ? `${moatsResults.length} of ${Math.min(moatsTotal, 100)} shown` : "No results yet"}
                {moatsCacheProgress?.warming && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] bg-yellow-500/10 text-yellow-500" data-testid="moats-warming-chip">
                    <RefreshCw className="w-2.5 h-2.5 animate-spin" />
                    warming cache · {moatsCacheProgress.cached_fresh}/{moatsCacheProgress.universe} fresh
                  </span>
                )}
              </span>
              <Button variant="outline" size="sm" onClick={() => loadMoats(0)} disabled={moatsLoading}
                className="h-7 text-[10px] rounded-sm" data-testid="moats-refresh-btn">
                <RefreshCw className={`w-3 h-3 mr-1 ${moatsLoading ? "animate-spin" : ""}`} /> Apply Filters
              </Button>
            </div>
          </div>

          <div className="border overflow-x-auto" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
            {moatsLoading && moatsResults.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-5 h-5 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />
                <span className="ml-2 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>Scanning universe…</span>
              </div>
            ) : (
              <table className="w-full dense-table" data-testid="moats-table">
                <thead className="sticky top-0 z-10" style={{ background: "var(--hoip-card)" }}>
                  <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                    <th className="text-left text-xs font-medium px-3 py-2" style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</th>
                    <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Sector</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Price</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>6m %</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>12m %</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>ROE</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }} title="Return on Invested Capital — shown when populated">ROIC</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Rev growth</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }} title="Debt-to-Equity — normalized to ratio form (≤ 1.0 means debt ≤ equity)">D/E</th>
                    <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Mkt Cap</th>
                    <th className="text-center text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {moatsResults.map((r, i) => {
                    return (
                      <tr key={r.ticker || i} className="border-b hover:bg-[var(--hoip-hover)] cursor-pointer transition-colors"
                        style={{ borderColor: "var(--hoip-divider)" }} onClick={() => navigate(`/ticker/${r.ticker}`)}
                        data-testid={`moats-row-${r.ticker}`}>
                        <td className="font-mono-data text-xs font-medium px-3 py-1.5 text-blue-400">{r.ticker}</td>
                        <td className="text-xs truncate max-w-[100px]" style={{ color: "var(--hoip-text-secondary)" }}>{r.sector}</td>
                        <td className="text-right font-mono-data text-xs">{r.price != null ? `$${r.price.toFixed(2)}` : "—"}</td>
                        <td className={`text-right font-mono-data text-xs ${r.trailing_6m_pct > 0 ? "text-emerald-500" : r.trailing_6m_pct < 0 ? "text-red-400" : ""}`}>
                          {r.trailing_6m_pct != null ? `${r.trailing_6m_pct.toFixed(1)}%` : "—"}
                        </td>
                        <td className={`text-right font-mono-data text-xs ${r.trailing_12m_pct > 0 ? "text-emerald-500" : r.trailing_12m_pct < 0 ? "text-red-400" : ""}`}>
                          {r.trailing_12m_pct != null ? `${r.trailing_12m_pct.toFixed(1)}%` : "—"}
                        </td>
                        <td className="text-right font-mono-data text-xs">
                          {r.roe != null ? `${(r.roe * 100).toFixed(1)}%` : "—"}
                        </td>
                        <td className="text-right font-mono-data text-xs" title="ROIC when available">
                          {r.roic != null ? `${(r.roic * 100).toFixed(1)}%` : "—"}
                        </td>
                        <td className="text-right font-mono-data text-xs">
                          {r.revenue_growth != null ? `${(r.revenue_growth * 100).toFixed(1)}%` : "—"}
                        </td>
                        <td className="text-right font-mono-data text-xs">
                          {r.debt_equity != null ? r.debt_equity.toFixed(2) : "—"}
                        </td>
                        <td className="text-right font-mono-data text-xs">
                          {r.market_cap != null ? `$${(r.market_cap / 1_000_000_000).toFixed(1)}B` : "—"}
                        </td>
                      </tr>
                    );
                  })}
                  {moatsResults.length === 0 && !moatsLoading && (
                    <tr><td colSpan={10} className="text-center py-8 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>
                      Click Apply Filters to scan.
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
            {/* Load more (paginate 20 → 40 → 60 → 80 → 100). */}
            {moatsResults.length > 0 && moatsResults.length < Math.min(moatsTotal, 100) && (
              <div className="p-3 flex justify-center border-t" style={{ borderColor: "var(--hoip-border)" }}>
                <Button variant="outline" size="sm" onClick={() => loadMoats(moatsOffset + 20)} disabled={moatsLoading}
                  className="h-7 text-[10px] rounded-sm" data-testid="moats-load-more-btn">
                  Load next 20
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
